import type {
  CompactAnalysisSummary,
  ModelComparisonPresentationSummary,
  ModelPresentationDeltas,
  ModelPresentationSummary,
  NumericDelta,
} from "@voxelspy/analysis";

import { formatNumber, truncateSafeText } from "./text.js";

/**
 * The accepted `Report` contract (packages/contracts/src/report.ts) has no
 * dedicated structured field for whole-model geometry summaries or their
 * baseline/candidate deltas -- `ModelComparisonPresentationSummary` is a
 * presentation-layer computation (`@voxelspy/analysis`'s
 * `summarizeModelComparison`), not part of any contracts schema. Injecting
 * it into `analysis.result.outcome.metrics` would misattribute
 * presentation-derived numbers as detector-computed evidence, which is the
 * kind of silent reinterpretation AGENTS.md asks reports to avoid.
 *
 * Until a maintainer accepts a structured field for this, the report
 * engine renders the summary as a clearly-labelled, deterministic
 * narrative into `review.notes` -- the one field the contract already
 * provides for exactly this kind of free-text content -- rather than
 * dropping it or fabricating a structured encoding. See the phase-1
 * handoff report for the recorded conflict.
 */
export function renderGeometrySummaryNotes(
  summary: ModelComparisonPresentationSummary,
): string {
  const lines = [
    "Automated geometry summary (generated by voxelspy.report-engine; not a human review note).",
    describeModel("Baseline", summary.baseline),
    describeModel("Candidate", summary.candidate),
    describeDeltas(summary.deltas),
    describeAnalysis(summary.analysis),
  ];
  return truncateSafeText(lines.join("\n"), 20_000);
}

function describeModel(label: string, model: ModelPresentationSummary): string {
  const bounds = model.bounds.available
    ? `dimensions ${formatNumber(model.bounds.dimensionsMillimetres[0])} x ${formatNumber(
        model.bounds.dimensionsMillimetres[1],
      )} x ${formatNumber(model.bounds.dimensionsMillimetres[2])} mm`
    : `dimensions unavailable (${model.bounds.reason})`;
  const volume = model.volume.available
    ? `volume ${formatNumber(model.volume.absoluteCubicMillimetres)} mm^3`
    : `volume unavailable (${model.volume.reasons.join(", ")})`;
  return (
    `${label}: vertices ${model.vertexCount}, triangles ${model.triangleCount}, ` +
    `meshes ${model.meshCount}, instances ${model.instanceCount}, components ${model.componentCount}, ` +
    `surface area ${formatNumber(model.surfaceAreaSquareMillimetres)} mm^2, ${volume}, ${bounds}.`
  );
}

function describeDelta(
  label: string,
  delta: NumericDelta,
  unit: string,
): string {
  return `${label} ${formatNumber(delta.difference)} ${unit} (${delta.direction})`;
}

function describeDeltas(deltas: ModelPresentationDeltas): string {
  const parts = [
    describeDelta("vertices", deltas.vertexCount, "count"),
    describeDelta("triangles", deltas.triangleCount, "count"),
    describeDelta("meshes", deltas.meshCount, "count"),
    describeDelta("instances", deltas.instanceCount, "count"),
    describeDelta("components", deltas.componentCount, "count"),
    describeDelta("surface area", deltas.surfaceAreaSquareMillimetres, "mm^2"),
    deltas.absoluteVolumeCubicMillimetres.available
      ? describeDelta("volume", deltas.absoluteVolumeCubicMillimetres, "mm^3")
      : `volume delta unavailable (missing for ${deltas.absoluteVolumeCubicMillimetres.unavailableFor.join(", ")})`,
  ];
  return `Deltas (candidate minus baseline): ${parts.join(", ")}.`;
}

function describeAnalysis(analysis: CompactAnalysisSummary): string {
  if (analysis.state === "indeterminate") {
    return (
      `Analysis: indeterminate (${analysis.code}) using ${analysis.methodId} v${analysis.methodVersion}; ` +
      `${analysis.warningCount} warning(s). Reasons: ${analysis.reasons.join("; ")}.`
    );
  }
  return (
    `Analysis: complete, ${analysis.semantics}, ${analysis.changeStatus} ` +
    `(${analysis.regionCount} region(s)) using ${analysis.methodId} v${analysis.methodVersion}; ` +
    `${analysis.warningCount} warning(s).`
  );
}
